export const SHARE_EXPIRY_STORAGE_KEY = 'miosegno.share.lastExpiryChoice'

export type ShareExpiryMode = 'DAY_1' | 'DAYS_7' | 'DAYS_30' | 'RETENTION' | 'CUSTOM'

export interface ShareExpiryChoice {
  mode: ShareExpiryMode
  customDate?: string
}

export const DEFAULT_SHARE_EXPIRY_CHOICE: ShareExpiryChoice = { mode: 'RETENTION' }

export function normalizeShareExpiryChoice(value: unknown): ShareExpiryChoice {
  if (!value || typeof value !== 'object') return DEFAULT_SHARE_EXPIRY_CHOICE
  const candidate = value as { mode?: unknown; customDate?: unknown }
  const mode = candidate.mode
  if (
    mode !== 'DAY_1'
    && mode !== 'DAYS_7'
    && mode !== 'DAYS_30'
    && mode !== 'RETENTION'
    && mode !== 'CUSTOM'
  ) return DEFAULT_SHARE_EXPIRY_CHOICE

  if (mode === 'CUSTOM') {
    const customDate = typeof candidate.customDate === 'string' ? candidate.customDate : ''
    return { mode, customDate }
  }
  return { mode }
}

export function loadShareExpiryChoice(): ShareExpiryChoice {
  try {
    const raw = window.localStorage.getItem(SHARE_EXPIRY_STORAGE_KEY)
    if (!raw) return DEFAULT_SHARE_EXPIRY_CHOICE
    return normalizeShareExpiryChoice(JSON.parse(raw))
  } catch {
    return DEFAULT_SHARE_EXPIRY_CHOICE
  }
}

export function saveShareExpiryChoice(choice: ShareExpiryChoice): void {
  try {
    window.localStorage.setItem(
      SHARE_EXPIRY_STORAGE_KEY,
      JSON.stringify(normalizeShareExpiryChoice(choice)),
    )
  } catch {
    // Browser storage is only a UX convenience; sharing itself must still work.
  }
}

/**
 * Converts the browser choice into the requested server timestamp.
 * null means "보관기간까지"; the backend applies the authoritative maximum.
 */
export function requestedShareExpiresAt(
  choice: ShareExpiryChoice,
  now = new Date(),
): string | null {
  const normalized = normalizeShareExpiryChoice(choice)
  if (normalized.mode === 'RETENTION') return null

  if (normalized.mode === 'CUSTOM') {
    if (!normalized.customDate || !/^\d{4}-\d{2}-\d{2}$/.test(normalized.customDate)) {
      throw new Error('공유 종료 날짜를 선택해 주세요.')
    }
    const end = new Date(`${normalized.customDate}T23:59:59.999`)
    if (Number.isNaN(end.getTime()) || end.getTime() <= now.getTime()) {
      throw new Error('공유 종료 날짜는 오늘 이후로 선택해 주세요.')
    }
    return end.toISOString()
  }

  const days = normalized.mode === 'DAY_1' ? 1 : normalized.mode === 'DAYS_7' ? 7 : 30
  return new Date(now.getTime() + days * 24 * 60 * 60 * 1000).toISOString()
}
